import { useState } from 'react';

import { Organization } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';
import { isPresignMFAExempt } from '@/shared/lib';

import { authModel } from '@/entities/auth';
import { organizationModel } from '@/entities/organization';

type UseOrganizationMFASwitchResult = {
  currentOrganizationName?: string;
  pendingOrganization?: Organization;
  requestSwitch: (organizationID: string) => void;
  cancelSwitch: () => void;
  completeSwitch: () => void;
};

/**
 * Holds a switch into an organization that requires two-step verification
 * until the account has it. The switch itself is the caller's, so cancelling
 * leaves the session exactly where it was.
 */
export const useOrganizationMFASwitch = (
  switchOrganization: (organizationID: string) => void,
): UseOrganizationMFASwitchResult => {
  const [pendingOrganizationID, setPendingOrganizationID] = useState('');

  const isMFAEnabled = useAppSelector(authModel.selectors.selectIsMFAEnabled);
  const mfaStatusLoading = useAppSelector(
    authModel.selectors.selectMFAStatusLoading,
  );
  const organizationEntities = useAppSelector(
    organizationModel.selectors.organizationSelectors.selectEntities,
  );
  const currentOrganization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  const requestSwitch = (organizationID: string) => {
    // Before the status is read "not enabled" means unknown, and prompting on
    // it would rotate an already-working secret.
    const isEnrollmentKnownMissing =
      mfaStatusLoading === 'succeeded' && !isMFAEnabled;

    if (
      !isPresignMFAExempt() &&
      isEnrollmentKnownMissing &&
      organizationEntities[organizationID]?.MFARequiredForActor
    ) {
      setPendingOrganizationID(organizationID);
      return;
    }

    switchOrganization(organizationID);
  };

  const completeSwitch = () => {
    switchOrganization(pendingOrganizationID);
    setPendingOrganizationID('');
  };

  return {
    currentOrganizationName: currentOrganization?.Name,
    pendingOrganization: organizationEntities[pendingOrganizationID],
    requestSwitch,
    cancelSwitch: () => setPendingOrganizationID(''),
    completeSwitch,
  };
};
